import React, { createContext, useContext, useMemo, useState } from 'react';
import { RootNode, TreeNode } from './TreeComponentSpace';
import {
  AssemblyComponentStatus,
  IAssembly,
  IAssemblyComponentInformation,
  IAssemblyStructure,
  IAssemblyStructureNode,
  IProduct,
} from '../../graphql/schema/p4types';
import { ExtendedAssemblyReservation } from '../../actions/product/AssemblyReservationFormAction';

export interface AssemblyTreeContextType {
  assembly: IAssembly;
  assemblyReservations: { [key: string]: ExtendedAssemblyReservation } | undefined;
  setAssembly: React.Dispatch<React.SetStateAction<IAssembly | null>>;
  setReservationAssembly: React.Dispatch<React.SetStateAction<IAssembly | null>>;
  assemblyNodes: RootNode<IAssemblyStructure>[];
  setAssemblyNodes: React.Dispatch<React.SetStateAction<Array<RootNode<IAssemblyStructure>>>>;
  assemblyProduct: IProduct;
  setAssemblyProduct: React.Dispatch<React.SetStateAction<IProduct | null>>;
  assemblyStructure: IAssemblyStructure;
  setAssemblyStructure: React.Dispatch<React.SetStateAction<IAssemblyStructure | null>>;
  selectedComponent: TreeNode<IAssemblyStructureNode>;
  setSelectedComponent: React.Dispatch<React.SetStateAction<TreeNode<IAssemblyStructureNode> | null>>;
  updatedNodes: string[];
  setUpdatedNodes: React.Dispatch<React.SetStateAction<Array<string>>>;
  expanded: string[];
  setExpanded: React.Dispatch<React.SetStateAction<Array<string>>>;
  reserveAll: boolean;
}

export interface AssemblyReservationDetailsContextType {
  reservationAssembly: IAssembly;
  setReservationAssembly: React.Dispatch<React.SetStateAction<IAssembly | null>>;
  assemblyReservations: { [key: string]: ExtendedAssemblyReservation } | undefined;
  setAssemblyReservations: React.Dispatch<React.SetStateAction<{ [key: string]: ExtendedAssemblyReservation } | null>>;
  assemblyComponentInformation: IAssemblyComponentInformation;
  setAssemblyComponentInformation: React.Dispatch<React.SetStateAction<IAssemblyComponentInformation>>;
  reserveAll: boolean;
  setReserveAll: React.Dispatch<React.SetStateAction<boolean>>;
  setUpdatedNodes: React.Dispatch<React.SetStateAction<Array<string>>>;
  component: any;
  assignedAssembly: IAssembly;
  unassignedStatus: boolean;
  selectedComponent: TreeNode<IAssemblyStructureNode>;
  rowPath: string;
}

export interface AssemblySubformContextType {
  assembly: IAssembly;
  assemblyComponentInformation: IAssemblyComponentInformation;
  rowPath: string;
}

export interface TreeComponentContextType {
  listItems: any[];
  form: React.ComponentType<any>;
  subForm?: React.ComponentType<any>;
  processEntities?: (fetchedEntities: any[]) => IAssemblyStructureNode[];
  constructNode: (processedEntity: IAssemblyStructureNode) => TreeNode<IAssemblyStructureNode>;
  initialNodes?: RootNode<IAssemblyStructure>[];
  updatedNodes?: string[];
  childrenGetter: () => IAssemblyStructureNode[];
  onSelectNode: (data: IAssemblyStructureNode) => void;
  onExpandNode: (data: any) => void;
  expanded: [];
  setExpanded: any;
  processExpandedNodes?: (nodes: TreeNode<IAssemblyStructureNode>[]) => void;
}

type AssemblyReservationsContextType =
  | AssemblyTreeContextType
  | AssemblyReservationDetailsContextType
  | AssemblySubformContextType
  | TreeComponentContextType;

const AssemblyTreeContext = createContext<AssemblyReservationsContextType | undefined>(undefined);

export const useAssemblyTreeContext = () => {
  const context = useContext(AssemblyTreeContext);
  if (!context) {
    throw new Error('useAssemblyTreeContext must be used within an AssemblyTreeProvider');
  }
  return context;
};

export const AssemblyTreeProvider = ({ children }) => {
  const [assembly, setAssembly] = useState<IAssembly>();
  const [reservationAssembly, setReservationAssembly] = useState<IAssembly>();
  const [assemblyNodes, setAssemblyNodes] = useState<Array<RootNode<IAssemblyStructure>>>([]);
  const [assemblyProduct, setAssemblyProduct] = useState<IProduct>();
  const [assemblyStructure, setAssemblyStructure] = useState<IAssemblyStructure>();
  const [assemblyReservations, setAssemblyReservations] = useState<
    { [key: string]: ExtendedAssemblyReservation } | undefined
  >(undefined);
  const [selectedComponent, setSelectedComponent] = useState<TreeNode<IAssemblyStructureNode> | null>(null);
  const [assemblyComponentInformation, setAssemblyComponentInformation] = useState<IAssemblyComponentInformation>();
  const [updatedNodes, setUpdatedNodes] = useState<Array<string>>([]);
  const [reserveAll, setReserveAll] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<Array<string>>([]);

  const contextValue = useMemo(() => {
    return {
      assembly,
      setAssembly,
      reservationAssembly,
      setReservationAssembly,
      assemblyNodes,
      setAssemblyNodes,
      assemblyProduct,
      setAssemblyProduct,
      assemblyStructure,
      setAssemblyStructure,
      assemblyReservations,
      setAssemblyReservations,
      selectedComponent,
      setSelectedComponent,
      assemblyComponentInformation,
      setAssemblyComponentInformation,
      updatedNodes,
      setUpdatedNodes,
      reserveAll,
      setReserveAll,
      expanded,
      setExpanded,
      listItems: assemblyStructure?.nodes,
      initialNodes: assemblyNodes,
      childrenGetter: () => assemblyStructure.nodes,
      rowPath: selectedComponent?.rowPath,
      component: assemblyStructure,
      assignedAssembly: selectedComponent?.assignedAssembly,
      unassignedStatus: selectedComponent?.assignedStatus === AssemblyComponentStatus.Unassigned,
    };
  }, [
    assembly,
    reservationAssembly,
    assemblyNodes,
    assemblyProduct,
    assemblyStructure,
    assemblyReservations,
    selectedComponent,
    assemblyComponentInformation,
    updatedNodes,
    reserveAll,
    expanded,
  ]);

  return <AssemblyTreeContext.Provider value={contextValue}>{children}</AssemblyTreeContext.Provider>;
};
